import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { KeyboardArrowDown, ContentCopy } from '@mui/icons-material';
import { colors } from '../../themes/typography';
import { experimentColors } from '../../themes/experimentColors';
import DiscountInput from './DiscountInput';
import ProductSelectionInline from './ProductSelectionInline';
import ComboDropdown from './ComboDropdown';

/**
 * Reusable combo section component
 * Extracted from ExperimentPanel for shared use across components
 */
const ComboSection = ({
  item,
  productKey,
  isEditable,
  selectedCombo,
  experiment,
  getEffectiveDiscount,
  calculateDiscountedPrice,
  apiResponseOverrides,
  customDiscounts,
  discountInputs,
  setDiscountInputs,
  pendingSaves,
  updatedStates,
  onComboSelect,
  onDiscountInputChange,
  onDiscountReset,
  onSaveChanges,
  t,
  productName, // Product name from adapter
  // Product selection props
  showProductSelection,
  onCustomProductSelect,
  onCancelProductSelection,
  onSelectNewProduct,
  clientId
}) => {
  // Local handler for combo selection
  const handleComboSelect = (productKey, combo) => {
    // Always use onComboSelect (required prop)
    if (onComboSelect) {
      onComboSelect(productKey, combo);
    }
  };

  // Handle copying combo name to clipboard
  const handleCopyCombo = async () => {
    if (!productName || !selectedCombo?.name) return;
    
    const comboText = `COMBO ${productName} + ${selectedCombo.name}`;
    
    try {
      await navigator.clipboard.writeText(comboText);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = comboText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <Box sx={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '8px',
      p: '6px',
      mb: '6px',
      border: `1px solid ${colors.border.light}`
    }}>
      {/* Header with title and copy button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '8px', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Typography sx={{
            fontSize: '9px',
            color: colors.text.primary,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            lineHeight: 1
          }}>
            {t('suggestedCombo')}
          </Typography>
          <ContentCopy 
            onClick={handleCopyCombo}
            sx={{ 
              color: colors.text.secondary, 
              fontSize: '10px',
              cursor: 'pointer',
              '&:hover': {
                color: colors.text.primary
              }
            }} 
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '6px' }}>
        <Tooltip title={productName} arrow placement="top">
          <Typography sx={{
            fontSize: '10px',
            color: colors.text.primary,
            fontWeight: 500,
            lineHeight: 1.2,
            maxWidth: '125px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {productName}
          </Typography>
        </Tooltip>
        <Typography sx={{
          fontSize: '10px',
          color: colors.text.primary,
          fontWeight: 500,
          lineHeight: 1.2,
          flexShrink: 0
        }}>
          +
        </Typography>
        {isEditable ? (
          <ComboDropdown
            comboPrices={experiment.comboPrices}
            selectedCombo={selectedCombo}
            onComboSelect={handleComboSelect}
            onSelectNewProduct={onSelectNewProduct}
            productKey={productKey}
            disabled={false}
            t={t}
          />
        ) : (
          <Tooltip title={selectedCombo?.name} arrow placement="top">
            <Typography sx={{
              fontSize: '10px',
              color: colors.text.primary,
              fontWeight: 700,
              lineHeight: 1.2,
              backgroundColor: colors.background.surface,
              border: `1px solid ${colors.border.light}`,
              borderRadius: '4px',
              px: '4px',
              py: '1px',
              maxWidth: '130px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {selectedCombo?.name}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {/* Show pricing comparison for experiments with combo pricing data */}
      {experiment?.comboPrices && selectedCombo && (() => {
        const comboData = experiment.comboPrices.find(c => c.candidateName === selectedCombo.name);
        
        if (comboData) {
          const originalPrice = comboData.totalWithoutDiscount || 0;
          const effectiveDiscount = getEffectiveDiscount(productKey, item, selectedCombo);
          
          // Check for API response overrides first
          const overrideKey = `${productKey}-${selectedCombo.name}`;
          let effectiveDiscountedPrice;
          
          if (apiResponseOverrides[overrideKey]) {
            effectiveDiscountedPrice = apiResponseOverrides[overrideKey].price || 0;
          } else if (customDiscounts[productKey] !== undefined) {
            effectiveDiscountedPrice = calculateDiscountedPrice(originalPrice, effectiveDiscount);  // User modified
          } else {
            effectiveDiscountedPrice = comboData.totalWithDiscount || 0;  // Use API value
          }
          
          // Ensure we have a valid number
          effectiveDiscountedPrice = effectiveDiscountedPrice || 0;
            
          // Initialize discount input if not set
          if (discountInputs[productKey] === undefined) {
            setDiscountInputs(prev => ({ ...prev, [productKey]: effectiveDiscount.toString() }));
          }
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px', flexWrap: 'wrap' }}>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.primary,
                fontWeight: 400,
                lineHeight: 1.2
              }}>
                €{(effectiveDiscountedPrice || 0).toFixed(2)}
              </Typography>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.secondary,
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                /
              </Typography>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.secondary,
                fontWeight: 500,
                lineHeight: 1.2,
                textDecoration: 'line-through'
              }}>
                €{(originalPrice || 0).toFixed(2)}
              </Typography>
              <Typography sx={{
                fontSize: '10px',
                color: colors.text.secondary,
                fontWeight: 500,
                lineHeight: 1.2
              }}>
                -
              </Typography>
              <DiscountInput
                productKey={productKey}
                isEditable={isEditable}
                discountInputs={discountInputs}
                effectiveDiscount={effectiveDiscount}
                pendingSaves={pendingSaves}
                onDiscountInputChange={onDiscountInputChange}
                onDiscountReset={onDiscountReset}
                onSaveChanges={onSaveChanges}
                t={t}
              />
            </Box>
          );
        }
        return null;
      })()}
      {updatedStates[productKey] && (
        <Typography sx={{
          fontSize: '9px',
          color: experimentColors.running,
          fontWeight: 600,
          mt: '2px',
          backgroundColor: `${experimentColors.running}15`,
          px: '4px',
          py: '1px',
          borderRadius: '4px'
        }}>
          ✓ Updated
        </Typography>
      )}
      
      {/* Product Selection Inline UI */}
      {showProductSelection && showProductSelection[productKey] && (
        <ProductSelectionInline
          show={showProductSelection[productKey]}
          onApply={(productId, productName, reason, price) => 
            onCustomProductSelect(productKey, productId, productName, reason, price, item, {[productKey]: {candidates: [{name: selectedCombo?.name}]}}, selectedCombo)
          }
          onCancel={() => onCancelProductSelection(productKey)}
          t={t}
          clientId={clientId}
        />
      )}
    </Box>
  );
};

export default ComboSection;
